import { TranslateService } from '@ngx-translate/core';
import { CartSizesPipe } from '@trp/community/shared/ui';
import {
  CommunityProfileWidgetConfig,
  SINGLE_FAMILY_VERIFIABLE_PROPERTIES,
} from '@trp/community/interfaces';
import {
  ContainersPipe,
  CurbsideContaminationPipe,
} from '@trp/community/shared/ui';
import { SingleFamilyProgram } from '@trp/nationaldb/interfaces';


const singleFamilyPrograms = (
  translateService: TranslateService,
  containersPipe: ContainersPipe,
  curbsideContaminationPipe: CurbsideContaminationPipe,
  cartSizesPipe: CartSizesPipe,
): CommunityProfileWidgetConfig['singleFamilyPrograms'] => ({
  section: 'singleFamilyPrograms',
  title: 'widgets.singleFamilyProgram.title',
  maxColumns: 3,
  propertiesToVerify: [...SINGLE_FAMILY_VERIFIABLE_PROPERTIES],
  widgets: [
    {
      section: 'singleFamilyPrograms',
      infoType: 'programUrl',
      type: 'standard',
      locations: ['community-profile', 'map-profile'],
      title: 'widgets.singleFamilyProgram.programUrl',
      icon: 'global:search',
      infoText: 'requestForm.questions.general.onlineToolForAcceptedMaterialsInfo',
      availableValueFormat: 'html',
      ifInfoAvailable: (info: SingleFamilyProgram) => !!info.programUrl,
      availableResolver: (info: SingleFamilyProgram) =>
        `<a href="${info.programUrl}" class="link" target="_blank">${info.programUrl}</a>`,
      unavailableResolver: () =>
        translateService.instant(
          'widgets.singleFamilyProgram.notice.noProgramUrl'
        ),
    },
    {
      section: 'singleFamilyPrograms',
      infoType: 'pdfUrl',
      type: 'standard',
      locations: ['community-profile', 'map-profile'],
      title: 'widgets.singleFamilyProgram.pdfUrl',
      icon: 'custom-solid:RoundedContentBallot',
      infoText: 'requestForm.questions.general.pdfUrlInfo',
      availableValueFormat: 'html',
      ifInfoAvailable: (info: SingleFamilyProgram) => !!info.pdfUrl,
      availableResolver: (info: SingleFamilyProgram) =>
        `<a href="${info.pdfUrl}" class="link" target="_blank">${info.pdfUrl}</a>`,
      unavailableResolver: () =>
        translateService.instant(
          'widgets.singleFamilyProgram.notice.noPdfUrl'
        ),
    },
    {
      section: 'singleFamilyPrograms',
      infoType: 'guidelinesUrl',
      type: 'standard',
      locations: ['community-profile', 'map-profile'],
      title: 'widgets.singleFamilyProgram.guidelinesUrl',
      icon: 'global:alternate-email',
      infoText: 'requestForm.questions.general.webpageForAcceptedMaterialInfo',
      availableValueFormat: 'html',
      ifInfoAvailable: (info: SingleFamilyProgram) => !!info.guidelinesUrl,
      availableResolver: (info: SingleFamilyProgram) =>
        `<a href="${info.guidelinesUrl}" class="link" target="_blank">${info.guidelinesUrl}</a>`,
      unavailableResolver: () =>
        translateService.instant(
          'widgets.singleFamilyProgram.notice.noGuidelinesUrl'
        ),
    },
    {
      section: 'singleFamilyPrograms',
      infoType: 'collectionHow',
      type: 'standard',

      locations: ['community-profile', 'map-profile'],
      title: 'widgets.singleFamilyProgram.collectionHow',
      icon: 'custom-solid:Container',
      ifInfoAvailable: (info: SingleFamilyProgram) => !!info.collectionHow,
      availableResolver: (info: SingleFamilyProgram) => info.collectionHow,
      unavailableResolver: () =>
        translateService.instant(
          'widgets.singleFamilyProgram.notice.noCollectionHowInfo'
        ),
    },
    {
      section: 'singleFamilyPrograms',
      infoType: 'collectionWho',
      type: 'standard',

      locations: ['community-profile', 'map-profile'],
      title: 'widgets.singleFamilyProgram.collectionWho',
      icon: 'custom-solid:Container',
      ifInfoAvailable: (info: SingleFamilyProgram) => !!info.collectionWho,
      availableResolver: (info: SingleFamilyProgram) => info.collectionWho,
      unavailableResolver: () =>
        translateService.instant(
          'widgets.singleFamilyProgram.notice.noCollectionWhoInfo'
        ),
    },
    {
      section: 'singleFamilyPrograms',
      infoType: 'collectionFrequency',
      type: 'standard',
      locations: ['community-profile', 'map-profile'],

      title: 'widgets.singleFamilyProgram.collectionFrequency',
      icon: 'global:SingleFamilyCollectionFrequency',
      infoText: 'requestForm.questions.singleFamily.collectionFrequencyInfo',
      ifInfoAvailable: (info: SingleFamilyProgram) =>
        !!info.collectionFrequency,
      availableResolver: (info: SingleFamilyProgram) =>
        info.collectionFrequency,
      unavailableResolver: () =>
        translateService.instant(
          'widgets.singleFamilyProgram.notice.noFrequencyInfo'
        ),
    },
    {
      section: 'singleFamilyPrograms',
      infoType: 'containerTypes',
      type: 'standard',
      locations: ['community-profile', 'map-profile'],
      infoText: 'requestForm.questions.singleFamily.containerTypesInfo',
      title: 'widgets.singleFamilyProgram.containerTypes',
      icon: 'custom-solid:Container',
      ifInfoAvailable: (info: SingleFamilyProgram) =>
        (info.containerTypes || []).length > 0,
      availableResolver: (info: SingleFamilyProgram) =>
        containersPipe.transform(
          info.containerTypes,
          translateService.instant(
            'widgets.singleFamilyProgram.notice.noContainerInfo'
          )
        ),
      unavailableResolver: () =>
        translateService.instant(
          'widgets.singleFamilyProgram.notice.noContainerInfo'
        ),
    },
    {
      section: 'singleFamilyPrograms',
      infoType: 'cartSizes',
      type: 'standard',
      locations: ['community-profile', 'map-profile'],
      availableValueFormat: 'html',
      title: 'widgets.singleFamilyProgram.cartSizes',
      icon: 'custom-solid:Container',
      ifInfoAvailable: (info: SingleFamilyProgram) =>
        (info.cartSizes || []).length > 0,
      availableResolver: (info: SingleFamilyProgram) =>
        cartSizesPipe.transform(info.cartSizes),
      unavailableResolver: () =>
        translateService.instant(
          'widgets.singleFamilyProgram.notice.noCartSizes'
        ),
    },
  ],
});

export default singleFamilyPrograms;
